import { FC, ReactElement } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import ChartTooltip from './ChartTooltip';

const COLORS = ['#41797b', '#99d9d9', '#d51668', '#f58c29', '#c3c3c3'];

type ReactionScoreProps = {
  data: {
    label: string;
    value: number;
  }[];
  label: string;
  subLabel?: string;
};

const ReactionScore: FC<ReactionScoreProps> = ({ data, label, subLabel }): ReactElement => {
  const renderLegend = (legendProps: any) => {
    if (!legendProps?.payload) return null;
    const total = data.reduce((sum, d) => sum + d.value, 0);

    return (
      <ul className="m-0 list-none p-0 text-sm">
        {legendProps.payload.map((entry: any, index: number) => {
          const itemValue = entry?.payload?.value ?? 0;
          const percent = total > 0 ? (itemValue / total) * 100 : 0;
          return (
            <li key={`legend-item-${index}`} className="flex items-center justify-between py-6">
              <div className="flex items-center gap-8">
                <span
                  className="inline-block h-12 w-12 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-neutral-500 text-xs">{entry.value}</span>
              </div>
              <span className="w-24 text-right text-neutral-500 text-xs">
                {percent.toFixed(0)}%
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div
      className={`
        relative flex w-438 h-306 p-20 flex-col items-start gap-2.5
        shrink-0 bg-white rounded-sm elevated
      `}
    >
      <div className="flex justify-between mb-10">
        <h3 className="text-h3">{label}</h3>
      </div>
      <div className="flex w-full">
        <ResponsiveContainer width="100%" height={210}>
          <PieChart>
            <Pie
              data={data}
              cx="43%"
              cy="48%"
              innerRadius={48}
              labelLine={false}
              label={false}
              animationBegin={0}
              outerRadius={100}
              fill="var(--color-secondary-navy-500)"
              nameKey={'label'}
              dataKey="value"
            >
              {data.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              width={126}
              iconSize={12}
              iconType="circle"
              wrapperStyle={{ right: 24 }}
              content={renderLegend}
            />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              content={<ChartTooltip showArrow={false} />}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <label className="flex items-center text-xs italic text-neutral-300 pt-4">{subLabel}</label>
    </div>
  );
};

export default ReactionScore;
